import type { OrganizationsFetchFailedAction } from '@Application/Organizations/Organizations/Actions/organizationsFetchFailed';
import FetchOrganizations from '@Application/Organizations/Organizations/Queries/FetchOrganizations';
import fetchOrganizations from '@Application/Organizations/Organizations/fetchOrganizations';
import { PermissionEnum } from '@Application/Security/Roles';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import OrganizationCard from '@UI/Pages/Administration/Organizations/OrganizationCard';
import { openOrganizationSidebar } from '@UI/Pages/Administration/Organizations/Signals/organizationSidebarSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import { css } from '@emotion/react';
import { Landmark, Plus } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, Spinner } from '@verclam/ui-kit';

const spinnerContainerStyle = css`
	display: flex;
	justify-content: center;
	padding: ${theme.layout.gutter};
`;

const cardsGridStyle = css`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(20rem, 100%), 1fr));
	gap: 1.25rem;
`;

const DEFAULT_LOAD_ERROR_MESSAGE = 'Une erreur est survenue pendant le chargement des structures.';

/**
 * The account's organizations, as a card grid — always a list, even reduced to a single card: there
 * is no "single organization" branch of its own. `account === null` is handled exclusively by
 * `AppShell`, above this page.
 */
const OrganizationsContent: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const canWrite = useHasPermission(PermissionEnum.ORGANIZATION_WRITE);

	// Captured separately from `useStoreQuery`'s own `error`: a rejected, non-`Error` action is
	// coerced to `new Error(String(action))` internally, which loses `payload.message` — the text
	// this screen must show verbatim.
	const [fetchErrorMessage, setFetchErrorMessage] = React.useState<string | null>(null);

	const {
		data: organizations,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.organizationsStore.organizations,
		stopOn: (value) => !!value && value.accountId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchOrganizations(new FetchOrganizations(accountId))).catch(
				(action: OrganizationsFetchFailedAction) => {
					setFetchErrorMessage(action.payload?.message ?? null);
					throw action;
				},
			);
		},
		deps: [accountId],
	});

	const handleCreateClick = React.useCallback(() => {
		openOrganizationSidebar(null);
	}, []);

	// Plain JSX, not memoized: it only wraps `handleCreateClick` (already stable).
	const createButton = canWrite ? (
		<Button
			type="button"
			label="Nouvelle structure"
			icon={<Plus size={16} />}
			onClick={handleCreateClick}
		/>
	) : undefined;

	if (organizations === null && error === null) {
		return (
			<div css={spinnerContainerStyle}>
				<Spinner aria-label="Chargement des structures" />
			</div>
		);
	}

	if (organizations === null) {
		return (
			<ErrorState
				title="Impossible de charger les structures"
				description={fetchErrorMessage ?? DEFAULT_LOAD_ERROR_MESSAGE}
				onRetry={refetch}
			/>
		);
	}

	if (organizations.list.length === 0) {
		return <EmptyState title="Aucune structure." icon={<Landmark size={28} />} action={createButton} />;
	}

	return (
		<div css={cardsGridStyle}>
			{organizations.list.map((organization) => (
				<OrganizationCard key={organization.id} organization={organization} />
			))}
		</div>
	);
};

export default OrganizationsContent;
